import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs, Timestamp, orderBy, or, and } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import type { Order } from '../types';
import WeatherWidget from '../components/WeatherWidget';
import { 
  Package,
  CreditCard,
  Truck,
  CheckCircle,
  XCircle,
  TrendingUp,
  Users,
  ShoppingBag,
  Clock,
  AlertCircle,
  Eye,
  AlertTriangle,
  Info
} from 'lucide-react';

interface Product {
  id: string;
  name: string;
  sku: string;
  baselinker_id: string;
  stockQuantity: number;
  mainImage: string;
}

type StockLevel = 'low' | 'medium' | 'good';

const STOCK_THRESHOLDS = {
  low: 10,     // Below 10 = low
  medium: 20   // Below 20 = medium, above = good
};

export default function Dashboard() {
  const [orderStats, setOrderStats] = useState({
    paid: 0,
    pending: 0,
    shipped: 0,
    delivered: 0,
    awaitingPayment: 0,
    preparation: 0,
    awaitingStock: 0,
    awaitingPickup: 0
  });
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);
  const [monthlyRevenue, setMonthlyRevenue] = useState(0);
  const [lastThreeMonthsRevenue, setLastThreeMonthsRevenue] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [salesData, setSalesData] = useState<{ month: string; revenue: number; }[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [stockCounts, setStockCounts] = useState<Record<StockLevel, number>>({
    low: 0,
    medium: 0,
    good: 0
  });
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser?.companyId) {
      loadDashboardData();
      loadStockData();
    }
  }, [currentUser]);

  useEffect(() => {
    loadSalesData();
  }, []);

  const calculateRevenue = (orders: Order[], months: number = 1) => {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth() - (months - 1), 1);
    
    console.log('Calculating revenue for period:', JSON.stringify({ startDate, now }, null, 2));
    console.log('Orders before filter:', orders.length);

    const filteredOrders = orders
      .filter(order => {
        // Verificar se o pedido pertence ao vendedor
        const temProdutosDoVendedor = 
          // Para vendas do marketplace
          order.items?.some((item: any) => item.companyId === currentUser?.companyId) ||
          // Para vendas manuais e privadas
          order.companyId === currentUser?.companyId ||
          order.sellerId === currentUser?.companyId;
        
        if (!temProdutosDoVendedor) {
          console.log('Order excluded - not seller:', order.id);
          return false;
        }

        // Verificar data
        const orderDate = order.createdAt instanceof Timestamp ? 
          order.createdAt.toDate() : 
          new Date(order.createdAt);
          
        const isInPeriod = orderDate >= startDate;
        if (!isInPeriod) {
          console.log('Order excluded - wrong date:', order.id, orderDate);
          return false;
        }

        return true;
      })
      .reduce((total, order) => {
        console.log('Processing order for revenue:', JSON.stringify({
          id: order.id,
          status: order.status,
          total: order.total,
          commission: order.commission,
          date: order.createdAt
        }, null, 2));

        // Only exclude cancelled orders
        if (order.status === 'cancelled') {
          console.log('Order excluded - cancelled:', order.id);
          return total;
        }
        const orderTotal = order.total || 0;
        const commission = order.commission || 0;
        const revenue = orderTotal - commission;
        console.log('Adding to revenue:', JSON.stringify({ orderTotal, commission, revenue }, null, 2));
        return total + revenue;
      }, 0);

    console.log('Final revenue:', filteredOrders);
    return filteredOrders;
  };

  const loadDashboardData = async () => {
    if (!currentUser?.companyId) {
      console.log('No company ID found');
      return;
    }

    try {
      console.log('Loading orders for company:', currentUser.companyId);
      // Get all orders
      const ordersRef = collection(db, 'orders');
      let ordersQuery;

      if (currentUser.role === 'admin') {
        // Admin sees all orders
        ordersQuery = query(ordersRef, orderBy('createdAt', 'desc'));
      } else {
        // Regular users see orders filtered by companyId
        ordersQuery = query(
          ordersRef,
          orderBy('createdAt', 'desc')
        );
      }
      
      const snapshot = await getDocs(ordersQuery);
      console.log('Found orders:', snapshot.size);
      const ordersData = await Promise.all(
        snapshot.docs
          .map(orderDoc => {
            const data = orderDoc.data() as Order;
            
            // Verificar se o pedido pertence ao vendedor
            const temProdutosDoVendedor = 
              // Para vendas do marketplace
              data.items?.some((item: any) => item.companyId === currentUser.companyId) ||
              // Para vendas manuais e privadas
              data.companyId === currentUser.companyId ||
              data.sellerId === currentUser.companyId;
            
            if (!temProdutosDoVendedor) {
              return null;
            }

            return {
              id: orderDoc.id,
              ...data,
              createdAt: data.createdAt instanceof Timestamp ? 
                data.createdAt.toDate().toISOString() : 
                data.createdAt,
              // Process WooCommerce order data
              buyerName: data.source === 'woocommerce' ? 
                `${data.sourceData?.billing?.first_name || ''} ${data.sourceData?.billing?.last_name || ''}`.trim() || 
                `${data.sourceData?.shipping?.first_name || ''} ${data.sourceData?.shipping?.last_name || ''}`.trim() || 
                'N/A' : 
                data.buyerName,
              total: data.source === 'woocommerce' ? 
                parseFloat(data.sourceData?.total || '0') : 
                data.total,
              orderNumber: data.source === 'woocommerce' ? 
                `WC${data.sourceData?.number || data.sourceOrderId}` : 
                data.orderNumber || data.orderId,
              type: data.source === 'woocommerce' ? 
                'WooCommerce' : 
                (data.isOnlineSale ? 'Marketplace' : data.isPrivateSale ? 'Privada' : 'Manual')
            };
          })
          .filter(Boolean) // Remove os nulls
          .map(async (orderData) => {
            let buyerName = orderData.buyerName;

            // First try to get name from shippingAddress
            if (orderData.shippingAddress?.name) {
              buyerName = orderData.shippingAddress.name;
            }
            // Then try customer info
            else if (orderData.customer?.name) {
              buyerName = orderData.customer.name;
            }
            // Then try buyer ID
            else if (orderData.buyerId) {
              try {
                const buyerDoc = await getDoc(doc(db, 'users', orderData.buyerId));
                if (buyerDoc.exists()) {
                  const buyerData = buyerDoc.data();
                  buyerName = buyerData.displayName || buyerData.email || buyerName;
                }
              } catch (err) {
                console.warn('Error loading buyer data:', err);
              }
            }
            // Finally try buyer company
            else if (orderData.buyerCompanyId) {
              try {
                const companyDoc = await getDoc(doc(db, 'companies', orderData.buyerCompanyId));
                if (companyDoc.exists()) {
                  const companyData = companyDoc.data();
                  buyerName = companyData.commercialName || companyData.legalName || buyerName;
                }
              } catch (err) {
                console.warn('Error loading buyer company data:', err);
              }
            }

            return {
              ...orderData,
              buyerName
            };
          })
      );

      console.log('Processed orders:', ordersData.length);

      // Sort by date
      ordersData.sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      // Calculate stats
      const stats = {
        pending: ordersData.filter(order => order.status === 'pending').length,
        shipped: ordersData.filter(order => order.status === 'shipped').length,
        delivered: ordersData.filter(order => order.status === 'delivered').length,
        awaitingPayment: ordersData.filter(order => order.status === 'awaiting_payment').length,
        preparation: ordersData.filter(order => order.status === 'preparation').length,
        awaitingStock: ordersData.filter(order => order.status === 'awaitingStock').length,
        awaitingPickup: ordersData.filter(order => order.status === 'awaitingPickup').length
      };
      setOrderStats(stats);

      // Calculate revenues
      const monthlyRev = calculateRevenue(ordersData);
      const threeMonthsRev = calculateRevenue(ordersData, 3);
      console.log('Monthly Revenue:', monthlyRev);
      console.log('Three Months Revenue:', threeMonthsRev);
      setMonthlyRevenue(monthlyRev);
      setLastThreeMonthsRevenue(threeMonthsRev);

      // Get recent orders
      const recentOrdersData = ordersData.slice(0, 10);
      console.log('Recent orders:', recentOrdersData.length);
      setRecentOrders(recentOrdersData);

      // Get total users count
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      setTotalUsers(usersSnapshot.size);

      setError('');
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadSalesData = async () => {
    try {
      const now = new Date();
      const monthsData = [];
      
      for (let i = 11; i >= 0; i--) {
        const startDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const endDate = new Date(now.getFullYear(), now.getMonth() - i + 1, 0, 23, 59, 59);
        
        // Query para vendas online (marketplace)
        const marketplaceQuery = query(
          collection(db, 'orders'),
          where('isOnlineSale', '==', true),
          where('createdAt', '>=', Timestamp.fromDate(startDate)),
          where('createdAt', '<=', Timestamp.fromDate(endDate))
        );
        
        // Query para vendas privadas
        const privateQuery = query(
          collection(db, 'orders'),
          where('isPrivateSale', '==', true),
          where('companyId', '==', currentUser?.companyId),
          where('createdAt', '>=', Timestamp.fromDate(startDate)),
          where('createdAt', '<=', Timestamp.fromDate(endDate))
        );
        
        // Query para vendas manuais
        const manualQuery = query(
          collection(db, 'orders'),
          where('isOnlineSale', '==', false),
          where('isPrivateSale', '==', false),
          where('companyId', '==', currentUser?.companyId),
          where('createdAt', '>=', Timestamp.fromDate(startDate)),
          where('createdAt', '<=', Timestamp.fromDate(endDate))
        );
        
        // Buscar todas as vendas
        const [marketplaceSnapshot, privateSnapshot, manualSnapshot] = await Promise.all([
          getDocs(marketplaceQuery),
          getDocs(privateQuery),
          getDocs(manualQuery)
        ]);
        
        // Combinar todos os pedidos
        const orders = [
          ...marketplaceSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Order)),
          ...privateSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Order)),
          ...manualSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Order))
        ];
        
        // Filtrar pedidos por companyId nos itens (para marketplace)
        const filteredOrders = orders.filter(order => {
          if (order.isOnlineSale) {
            const orderCompanyId = order.items?.[0]?.companyId;
            return orderCompanyId === currentUser?.companyId;
          }
          return true;
        });
        
        // Calcular receita do mês
        const monthRevenue = filteredOrders.reduce((acc, order) => {
          if (order.status !== 'cancelled') {
            const orderTotal = order.total || 0;
            const commission = order.commission || 0;
            return acc + (orderTotal - commission);
          }
          return acc;
        }, 0);
        
        monthsData.push({
          month: startDate.toLocaleDateString('pt-PT', { month: 'short' }).toUpperCase(),
          revenue: monthRevenue
        });
      }
      
      console.log('Sales Data:', monthsData); // Debug
      setSalesData(monthsData);
    } catch (error) {
      console.error('Error loading sales data:', error);
    }
  };

  const getStockLevel = (quantity: number): StockLevel => {
    if (quantity < STOCK_THRESHOLDS.low) return 'low';
    if (quantity < STOCK_THRESHOLDS.medium) return 'medium';
    return 'good';
  };

  const loadStockData = async () => {
    try {
      const productsRef = collection(db, 'products');
      
      // First get the logged-in user's products
      const userProductsQuery = query(
        productsRef,
        where('companyId', '==', currentUser?.companyId)
      );
      
      const userProductsSnapshot = await getDocs(userProductsQuery);
      const userBaselinkerIds = userProductsSnapshot.docs
        .map(doc => doc.data().baselinker_id)
        .filter(id => id); // Filter out null/undefined/empty values
      
      // Dividir os IDs em lotes de 30
      const batchSize = 30;
      const batches = [];
      for (let i = 0; i < userBaselinkerIds.length; i += batchSize) {
        batches.push(userBaselinkerIds.slice(i, i + batchSize));
      }

      // Fazer consultas para cada lote
      const allProducts = [];
      for (const batch of batches) {
        const q = query(
          productsRef,
          where('baselinker_id', 'in', batch.length > 0 ? batch : ['no_results'])
        );
        const querySnapshot = await getDocs(q);
        allProducts.push(...querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      }

      const productsData = allProducts as Product[];

      setProducts(productsData);

      // Calculate stock counts
      const counts = productsData.reduce((acc, product) => {
        const level = getStockLevel(product.stockQuantity);
        acc[level] = (acc[level] || 0) + 1;
        return acc;
      }, {} as Record<StockLevel, number>);

      setStockCounts(counts);

    } catch (err) {
      console.error('Error loading stock data:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
        {/* Ganhos Este Mês */}
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500 flex items-center gap-1">
              <TrendingUp className="w-4 h-4 text-[#a4c639]" />
              Ganhos Este Mês
            </h3>
          </div>
          <p className="text-2xl font-bold text-[#a4c639]">€{monthlyRevenue.toFixed(2)}</p>
        </div>

        {/* Últimos 3 Meses */}
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500 flex items-center gap-1">
              <TrendingUp className="w-4 h-4 text-[#a4c639]" />
              Últimos 3 Meses
            </h3>
          </div>
          <p className="text-2xl font-bold text-[#a4c639]">€{lastThreeMonthsRevenue.toFixed(2)}</p>
        </div>

        {/* Utilizadores ETC */}
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500 flex items-center gap-1">
              <Users className="w-4 h-4 text-[#a4c639]" />
              Utilizadores ETC
              <div className="relative group">
                <Info className="w-4 h-4 text-gray-400 cursor-help" />
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                  EMTUACASA
                </div>
              </div>
            </h3>
          </div>
          <p className="text-2xl font-bold text-[#a4c639]">{totalUsers}</p>
        </div>
      </div>

      {/* Order Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4">
        {/* Aguardar Pagamento */}
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <div className="flex items-center gap-2 text-gray-500 mb-2">
            <CreditCard className="w-4 h-4" />
            <span className="text-sm">Aguardar Pagamento</span>
          </div>
          <p className="text-xl font-semibold">{orderStats.awaitingPayment}</p>
        </div>

        {/* Aguardar Envio */}
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <div className="flex items-center gap-2 text-gray-500 mb-2">
            <Clock className="w-4 h-4" />
            <span className="text-sm">Aguardar Envio</span>
          </div>
          <p className="text-xl font-semibold">{orderStats.pending}</p>
        </div>

        {/* Enviadas */}
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <div className="flex items-center gap-2 text-gray-500 mb-2">
            <Truck className="w-4 h-4" />
            <span className="text-sm">Enviadas</span>
          </div>
          <p className="text-xl font-semibold">{orderStats.shipped}</p>
        </div>

        {/* Preparação */}
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <div className="flex items-center gap-2 text-gray-500 mb-2">
            <ShoppingBag className="w-4 h-4" />
            <span className="text-sm">Preparação</span>
          </div>
          <p className="text-xl font-semibold">{orderStats.preparation}</p>
        </div>

        {/* Aguardar Stock */}
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <div className="flex items-center gap-2 text-gray-500 mb-2">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">Aguardar Stock</span>
          </div>
          <p className="text-xl font-semibold">{orderStats.awaitingStock}</p>
        </div>
      </div>

      {/* Weather and Stock Status Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        {/* Weather Forecast */}
        <div className="bg-white p-4 rounded-lg shadow-md">
          <h2 className="text-lg font-semibold mb-4 text-gray-700">Previsão do Tempo</h2>
          <WeatherWidget />
        </div>

        {/* Stock Status */}
        <div className="bg-white p-4 rounded-lg shadow-md">
          <h2 className="text-lg font-semibold mb-4 text-gray-700">Estado do Stock</h2>
          <div className="space-y-4">
            {/* Low Stock */}
            <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertTriangle className="text-red-500" size={20} />
                <div>
                  <div className="text-sm text-gray-500">Stock Baixo</div>
                  <div className="text-lg font-semibold text-red-600">{stockCounts.low || 0} produtos</div>
                </div>
              </div>
              <button 
                onClick={() => navigate('/dashboard/fulfillment/stock')}
                className="text-red-600 hover:text-red-700"
              >
                <Eye size={20} />
              </button>
            </div>

            {/* Medium Stock */}
            <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertTriangle className="text-yellow-500" size={20} />
                <div>
                  <div className="text-sm text-gray-500">Stock Médio</div>
                  <div className="text-lg font-semibold text-yellow-600">{stockCounts.medium || 0} produtos</div>
                </div>
              </div>
              <button 
                onClick={() => navigate('/dashboard/fulfillment/stock')}
                className="text-yellow-600 hover:text-yellow-700"
              >
                <Eye size={20} />
              </button>
            </div>

            {/* Good Stock */}
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Package className="text-green-500" size={20} />
                <div>
                  <div className="text-sm text-gray-500">Stock Bom</div>
                  <div className="text-lg font-semibold text-green-600">{stockCounts.good || 0} produtos</div>
                </div>
              </div>
              <button 
                onClick={() => navigate('/dashboard/fulfillment/stock')}
                className="text-green-600 hover:text-green-700"
              >
                <Eye size={20} />
              </button>
            </div>

            {/* Product Summary */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <div className="text-sm text-gray-500">Total de Produtos</div>
                  <div className="text-lg font-semibold text-blue-600">
                    {products.length} produtos
                  </div>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg">
                  <div className="text-sm text-gray-500">Produtos Ativos</div>
                  <div className="text-lg font-semibold text-purple-600">
                    {products.filter(p => p.stockQuantity > 0).length} produtos
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Orders */}
      <div className="mt-8">
        <div className="bg-white p-4 rounded-lg shadow-md">
          <h2 className="text-lg font-semibold mb-4 text-gray-700">Últimas Encomendas</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CLIENTE</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">TOTAL</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ESTADO</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">DATA</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">TIPO</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">AÇÕES</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recentOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {order.orderNumber || order.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {order.buyerName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      €{(order.total || 0).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        order.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                        order.status === 'shipped' ? 'bg-purple-100 text-purple-800' :
                        order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                        order.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                        order.status === 'preparation' ? 'bg-orange-100 text-orange-800' :
                        order.status === 'awaitingStock' ? 'bg-orange-100 text-orange-800' :
                        order.status === 'awaitingPickup' ? 'bg-orange-100 text-orange-800' :
                        order.status === 'returned' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {order.status === 'pending' ? 'Pendente' :
                         order.status === 'processing' ? 'Em Processamento' :
                         order.status === 'shipped' ? 'Enviada' :
                         order.status === 'delivered' ? 'Entregue' :
                         order.status === 'cancelled' ? 'Cancelada' :
                         order.status === 'awaiting_payment' ? 'Aguardando Pagamento' :
                         order.status === 'preparation' ? 'Preparação' :
                         order.status === 'awaitingStock' ? 'Aguardar Stock' :
                         order.status === 'awaitingPickup' ? 'Aguardar Levantamento' :
                         order.status === 'returned' ? 'Devolvida' :
                         order.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(order.createdAt).toLocaleDateString('pt-PT')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        order.source === 'woocommerce' ? 'bg-orange-100 text-orange-800' :
                        order.isOnlineSale ? 'bg-blue-100 text-blue-800' :
                        order.isPrivateSale ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {order.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <button
                        onClick={() => navigate(`/dashboard/orders/${order.id}`)}
                        className="text-primary hover:text-primary-dark"
                      >
                        <Eye size={20} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}